import { useState } from "react";
import { Link } from "react-router-dom";
import type { MapSummary } from "shared";
import { mapsApi } from "@/api/maps";
import { useApi } from "@/api/hooks";
import { Button } from "@/components/primitives/Button";
import { FloatingCard } from "@/components/primitives/FloatingCard";
import { TextInput } from "@/components/forms/TextInput";
import { useToast } from "@/components/feedback/Toast";

export default function Dashboard() {
	const { toast } = useToast();
	const { data, loading, error, reload } = useApi(() => mapsApi.list(), []);
	const [title, setTitle] = useState("");
	const [busy, setBusy] = useState(false);

	async function createMap() {
		const t = title.trim();
		if (!t) return;
		setBusy(true);
		try {
			const { map } = await mapsApi.create({
				title: t,
				viewState: { center: [10.45, 51.16], zoom: 5.5 },
			});
			setTitle("");
			toast("Karte erstellt", "success");
			await reload();
			window.location.href = `/k/${map.id}`;
		} catch (e) {
			toast(e instanceof Error ? e.message : "Fehler", "error");
		} finally {
			setBusy(false);
		}
	}

	const maps: MapSummary[] = data?.maps ?? [];

	return (
		<div className="flex flex-1 items-start justify-center px-4 py-8 sm:justify-start sm:px-8">
			<FloatingCard className="w-full max-w-md p-4">
				<div>
					<h1 className="text-lg font-semibold tracking-tight">Meine Karten</h1>
					<p className="mt-0.5 text-sm text-fg-muted">
						Anlegen, öffnen, Ebenen und Einträge zeichnen.
					</p>
				</div>

				<div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
					<div className="min-w-0 flex-1">
						<TextInput
							label="Neue Karte"
							placeholder="z. B. Spielplätze im Viertel"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") void createMap();
							}}
						/>
					</div>
					<Button
						size="sm"
						className="sm:mb-0.5"
						disabled={busy || !title.trim()}
						onClick={() => void createMap()}
					>
						Anlegen
					</Button>
				</div>

				{loading ? (
					<p className="mt-6 text-sm text-fg-muted">Laden…</p>
				) : error ? (
					<p className="mt-6 text-sm text-rose-500">{error.message}</p>
				) : maps.length === 0 ? (
					<div className="mt-6 rounded-[var(--radius-control)] bg-surface-raised px-4 py-8 text-center">
						<p className="text-sm font-medium text-fg">Noch keine Karten</p>
						<p className="mt-1 text-xs text-fg-muted">
							Lege oben deine erste Karte an.
						</p>
					</div>
				) : (
					<ul className="mt-3 space-y-0.5">
						{maps.map((m) => (
							<li key={m.id}>
								<Link
									to={`/k/${m.id}`}
									className="flex h-10 items-center justify-between gap-3 rounded-[var(--radius-control)] px-2.5 hover:bg-zinc-950/[0.04] dark:hover:bg-white/[0.06]"
								>
									<div className="min-w-0">
										<p className="truncate text-sm font-medium text-fg">{m.title}</p>
										<p className="truncate text-xs text-fg-muted">
											{m.visibility} · {m.baseStyle}
										</p>
									</div>
									<span className="shrink-0 text-xs font-medium text-teal-600 dark:text-teal-400">
										Öffnen
									</span>
								</Link>
							</li>
						))}
					</ul>
				)}
			</FloatingCard>
		</div>
	);
}
